import { Box, Button, Chip, Divider, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import { ChartItemComponentTypes } from './ChartViewEditor';
import { useMemo } from 'react';
import { getChartIconByType, getChartLabelByType, getMiniChartByType } from './ChartItemComponent';
import { ChartType } from 'constant/ChartViewConstant';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';

// ── Styles ──

const textInputStyles = {
  '& .MuiOutlinedInput-root': {
    padding: '1px 2px',
    fontSize: 13,
  },
};

const sectionBoxSx = {
  bgcolor: '#f8f9fb',
  borderRadius: 1.5,
  border: '1px solid #eef0f4',
  p: 1.5,
};

// ── Types ──

interface ChartEditorComponentFormProps {
  index: number;
  onDelete: (index: number) => void;
  formik: ReturnType<
    typeof useFormik<{
      charts: ChartItemComponentTypes[];
    }>
  >;
}

// ── Axis Options ──

const AXIS_OPTIONS: AutoCompleteItem[] = [
  { value: 'STATIC_TIME_PERIODS', label: 'Static Time Periods' },
  { value: 'DATA_TYPE', label: 'Data Type' },
];

const AXIS_ICONS: Record<string, React.ReactNode> = {
  STATIC_TIME_PERIODS: <TimelineOutlinedIcon sx={{ fontSize: 16, color: '#3B4797' }} />,
  DATA_TYPE: <TableChartOutlinedIcon sx={{ fontSize: 16, color: '#3B4797' }} />,
};

// ── Sub-components ──

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant='input-label' sx={{ color: '#333', fontWeight: 700, fontSize: 12, mb: 0.25 }}>
      {children}
    </Typography>
  );
}

function SectionHint({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant='caption' sx={{ color: '#999', fontSize: 11, mb: 0.5, lineHeight: 1.3 }}>
      {children}
    </Typography>
  );
}

function AxisOptionChip({
  option,
  selected,
  onClick,
}: {
  option: AutoCompleteItem;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <Chip
      icon={AXIS_ICONS[option.value as string] as React.ReactElement}
      label={option.label}
      variant={selected ? 'filled' : 'outlined'}
      color={selected ? 'primary' : 'default'}
      onClick={onClick}
      sx={{
        fontWeight: selected ? 700 : 500,
        fontSize: 12,
        height: 32,
        cursor: 'pointer',
        transition: 'all 0.15s ease',
      }}
    />
  );
}

// ── Main Component ──

export function ChartEditorComponentForm(props: ChartEditorComponentFormProps) {
  const { formik, index } = props;
  const chart = formik.values.charts[index];

  const xAxisOptions = useMemo(() => {
    if (index === null) return AXIS_OPTIONS;
    return AXIS_OPTIONS.filter((option) => option.value !== chart?.yAxis?.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart?.yAxis, index]);

  const yAxisOptions = useMemo(() => {
    if (index === null) return AXIS_OPTIONS;
    return AXIS_OPTIONS.filter((option) => option.value !== chart?.xAxis?.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart?.xAxis, index]);

  if (index === null || !chart) {
    return null;
  }

  const chartType = chart.chartType;
  const isBarOrLine = chartType === ChartType.BAR || chartType === ChartType.LINE;
  const isGauge = chartType === ChartType.GAUGE;
  const isPieOrRow = chartType === ChartType.PIE || chartType === ChartType.ROW;

  const handleOnDeleteClick = () => {
    const confirm = window.confirm('Are you sure you want to delete this chart?');
    if (confirm) {
      props.onDelete(index);
    }
  };

  const handleAddGaugeRange = () => {
    const currentParams = chart.gaugeParams || [];
    const lastMax = currentParams.length > 0 ? currentParams[currentParams.length - 1].max : 0;
    formik.setFieldValue(`charts[${index}].gaugeParams`, [
      ...currentParams,
      { min: lastMax + 1, max: lastMax + 100, color: '#42BB93', label: null },
    ]);
  };

  const handleRemoveGaugeRange = (gaugeIndex: number) => {
    const updated = chart.gaugeParams.filter((_, i) => i !== gaugeIndex);
    formik.setFieldValue(`charts[${index}].gaugeParams`, updated);
  };

  return (
    <Stack width='100%' spacing={1.5}>
      {/* ── Header: chart type + mini preview ── */}
      <Stack
        direction='row'
        alignItems='center'
        gap={1.5}
        sx={{
          bgcolor: '#f0faf6',
          border: '1px solid #d4ede4',
          borderRadius: 2,
          px: 1.5,
          py: 1,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 40,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#fff',
            border: '1px solid #d4ede4',
            flexShrink: 0,
          }}
        >
          {getChartIconByType(chartType)}
        </Box>
        <Stack flex={1} minWidth={0}>
          <Typography sx={{ color: '#333', fontWeight: 700, fontSize: 13, lineHeight: 1.2 }}>
            {getChartLabelByType(chartType)}
          </Typography>
          <Typography sx={{ color: '#888', fontSize: 11 }}>{chart.name || 'Untitled'}</Typography>
        </Stack>
      </Stack>

      {/* ── General: Title & Description ── */}
      <Box sx={sectionBoxSx}>
        <Typography sx={{ fontWeight: 700, fontSize: 12, color: '#3B4797', mb: 1 }}>General</Typography>
        <Stack spacing={1.5}>
          <Stack>
            <SectionLabel>Chart Title</SectionLabel>
            <SectionHint>The name displayed at the top of this chart.</SectionHint>
            <TextField
              sx={textInputStyles}
              name={`charts[${index}].name`}
              value={chart.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              hiddenLabel
              variant='outlined'
              size='small'
              fullWidth
              placeholder='e.g. Revenue Over Time'
            />
          </Stack>
          <Stack>
            <SectionLabel>Description</SectionLabel>
            <SectionHint>A short summary of what this chart shows.</SectionHint>
            <TextField
              sx={textInputStyles}
              name={`charts[${index}].description`}
              value={chart.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              hiddenLabel
              variant='outlined'
              size='small'
              fullWidth
              multiline
              minRows={2}
              maxRows={3}
              placeholder='e.g. Monthly revenue breakdown by category'
            />
          </Stack>
        </Stack>
      </Box>

      {/* ── Axis Config: Bar & Line charts ── */}
      {isBarOrLine && (
        <Box sx={sectionBoxSx}>
          <Typography sx={{ fontWeight: 700, fontSize: 12, color: '#3B4797', mb: 1 }}>Axis Configuration</Typography>
          <Stack spacing={1.5}>
            <Stack>
              <SectionLabel>X-Axis</SectionLabel>
              <SectionHint>What to display along the horizontal axis.</SectionHint>
              <Stack direction='row' gap={0.75} flexWrap='wrap'>
                {xAxisOptions.map((option) => (
                  <AxisOptionChip
                    key={option.value as string}
                    option={option}
                    selected={chart.xAxis?.value === option.value}
                    onClick={() => formik.setFieldValue(`charts[${index}].xAxis`, option)}
                  />
                ))}
              </Stack>
            </Stack>
            <Divider sx={{ borderStyle: 'dashed' }} />
            <Stack>
              <SectionLabel>Y-Axis</SectionLabel>
              <SectionHint>What to display along the vertical axis.</SectionHint>
              <Stack direction='row' gap={0.75} flexWrap='wrap'>
                {yAxisOptions.map((option) => (
                  <AxisOptionChip
                    key={option.value as string}
                    option={option}
                    selected={chart.yAxis?.value === option.value}
                    onClick={() => formik.setFieldValue(`charts[${index}].yAxis`, option)}
                  />
                ))}
              </Stack>
            </Stack>
          </Stack>
        </Box>
      )}

      {/* ── Pie / Row: info section ── */}
      {isPieOrRow && (
        <Box sx={sectionBoxSx}>
          <Typography sx={{ fontWeight: 700, fontSize: 12, color: '#3B4797', mb: 1 }}>Display</Typography>
          <Stack
            direction='row'
            alignItems='center'
            gap={1}
            sx={{ p: 1, bgcolor: '#fff', borderRadius: 1, border: '1px solid #eef0f4' }}
          >
            <Box sx={{ width: 48, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {getMiniChartByType(chartType)}
            </Box>
            <Typography sx={{ color: '#666', fontSize: 12 }}>
              {chartType === ChartType.PIE
                ? 'Slices are auto-calculated from your data. Configure members and measures in the panel options.'
                : 'Rows are auto-calculated from your data. Configure members and measures in the panel options.'}
            </Typography>
          </Stack>
        </Box>
      )}

      {/* ── Gauge Ranges ── */}
      {isGauge && (
        <Box sx={sectionBoxSx}>
          <Stack direction='row' justifyContent='space-between' alignItems='center' mb={1}>
            <Typography sx={{ fontWeight: 700, fontSize: 12, color: '#3B4797' }}>Gauge Ranges</Typography>
            <Tooltip title='Add range' arrow>
              <IconButton size='small' onClick={handleAddGaugeRange} sx={{ color: '#42BB93' }}>
                <AddCircleOutlineIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </Stack>

          {chart.gaugeParams?.length > 0 && (
            <Stack spacing={1}>
              {/* Column headers */}
              <Stack direction='row' gap={0.5} px={0.5}>
                <Typography
                  sx={{ width: 40, fontSize: 10, color: '#999', fontWeight: 600, textTransform: 'uppercase' }}
                >
                  Color
                </Typography>
                <Typography sx={{ flex: 1, fontSize: 10, color: '#999', fontWeight: 600, textTransform: 'uppercase' }}>
                  Min
                </Typography>
                <Typography sx={{ flex: 1, fontSize: 10, color: '#999', fontWeight: 600, textTransform: 'uppercase' }}>
                  Max
                </Typography>
                <Box sx={{ width: 28 }} />
              </Stack>

              {chart.gaugeParams.map((gauge, gaugeIndex) => (
                <Stack
                  key={gaugeIndex}
                  direction='row'
                  alignItems='center'
                  gap={0.5}
                  sx={{
                    bgcolor: '#fff',
                    borderRadius: 1,
                    border: '1px solid #eef0f4',
                    p: 0.75,
                  }}
                >
                  {/* Color swatch */}
                  <Tooltip title='Pick color' arrow>
                    <Box sx={{ position: 'relative', width: 32, height: 32, flexShrink: 0 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1,
                          bgcolor: gauge.color,
                          border: '1px solid #ddd',
                          cursor: 'pointer',
                        }}
                      />
                      <TextField
                        name={`charts[${index}].gaugeParams[${gaugeIndex}].color`}
                        value={gauge.color}
                        onChange={formik.handleChange}
                        type='color'
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          opacity: 0,
                          cursor: 'pointer',
                          '& input': { cursor: 'pointer', p: 0, height: '100%' },
                        }}
                      />
                    </Box>
                  </Tooltip>

                  {/* Min */}
                  <TextField
                    sx={textInputStyles}
                    name={`charts[${index}].gaugeParams[${gaugeIndex}].min`}
                    value={gauge.min}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    variant='outlined'
                    size='small'
                    type='number'
                    placeholder='Min'
                    inputProps={{ style: { textAlign: 'center' } }}
                  />

                  {/* Max */}
                  <TextField
                    sx={textInputStyles}
                    name={`charts[${index}].gaugeParams[${gaugeIndex}].max`}
                    value={gauge.max}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    variant='outlined'
                    size='small'
                    type='number'
                    placeholder='Max'
                    inputProps={{ style: { textAlign: 'center' } }}
                  />

                  {/* Remove */}
                  <Tooltip title='Remove range' arrow>
                    <IconButton
                      size='small'
                      onClick={() => handleRemoveGaugeRange(gaugeIndex)}
                      sx={{ color: '#bbb', '&:hover': { color: '#e05555' }, flexShrink: 0 }}
                    >
                      <RemoveCircleOutlineIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ))}
            </Stack>
          )}

          {(!chart.gaugeParams || chart.gaugeParams.length === 0) && (
            <Stack alignItems='center' py={2} sx={{ bgcolor: '#fff', borderRadius: 1, border: '1px dashed #ddd' }}>
              <Typography sx={{ color: '#aaa', fontSize: 12 }}>No ranges defined</Typography>
              <Button size='small' startIcon={<AddCircleOutlineIcon />} onClick={handleAddGaugeRange} sx={{ mt: 0.5 }}>
                Add Range
              </Button>
            </Stack>
          )}
        </Box>
      )}

      {/* ── Footer: Delete ── */}
      <Divider />
      <Button
        onClick={handleOnDeleteClick}
        startIcon={<DeleteOutlinedIcon />}
        variant='outlined'
        color='error'
        size='small'
        fullWidth
      >
        Delete Chart
      </Button>
    </Stack>
  );
}
